import logo from "@app/assets/images/logo.svg";
import { links } from "@app/constants";
import type { RootState } from "@app/redux";
import React from "react";
import { useSelector } from "react-redux";
import "./footer.css";
import "./footer.responsive.css";
import messages from "./messages";

const FooterView = React.memo(() => {
	const { language } = useSelector(
		(rootState: RootState) => rootState.language,
	);

	const footerTextStyle = [
		"footer-text-primary",
		"opacity-70",
		"lg:opacity-100",
	].join(" ");

	const headingStyle = [
		"font-primary-bold",
		"leading-7",
		"lg:tracking-normal",
		"text-lg",
		"tracking-wider",
		"whitespace-nowrap",
		"footer-link-heading",
	].join(" ");

	const linkStyle = [
		"font-primary-light",
		"hover:underline",
		"leading-normal",
		"lg:text-base",
		"text-xl",
		footerTextStyle,
	].join(" ");

	const addressStyle = [
		"flex",
		"flex-col",
		"gap-y-2",
		"mt-8",
		linkStyle,
		"inline-block",
	].join(" ");

	const paragraphStyle = [
		"flex",
		"flex-col",
		"gap-y-5",
		"leading-normal",
		"text-xs",
		footerTextStyle,
		"lg:opacity-70",
	].join(" ");

	const sloganStyle = [
		"flex",
		"flex-col",
		"leading-8",
		"mt-11",
		"mb-14",
		footerTextStyle,
	].join(" ");

	const stackStyle = [
		"basis-auto",
		"flex",
		"flex-col",
		"gap-y-2",
		"items-start",
	].join(" ");

	const underlineStyle = ["mb-6", "footer-stack-underline"].join(" ");

	return (
		<div className="font-primary-light px-7 lg:px-24 py-20 lg:py-24 footer">
			<div className="flex flex-col gap-x-8 lg:flex-row justify-start">
				<div className="basis-1/3 shrink-0">
					<img
						src={logo}
						alt={messages[language].altLogo}
						className="max-w-full max-w-"
					/>
					<div className={sloganStyle}>
						<span>{messages[language].sloganLine1}</span>
						<span>{messages[language].sloganLine2}</span>
					</div>
				</div>
				<div className="basis-2/3 flex flex-col shrink lg:flex-row gap-x-6 gap-y-16 px-0 justify-between">
					<div className={stackStyle}>
						<div className={headingStyle}>
							{messages[language].quickLinks.heading}
						</div>
						<div className={underlineStyle}></div>
						<a
							className={linkStyle}
							href={links.quickLinks.about}
							target={"_blank"}
							rel="noreferrer"
						>
							{messages[language].quickLinks.about}
						</a>
						<a
							className={linkStyle}
							href={links.quickLinks.faqs}
							target={"_blank"}
							rel="noreferrer"
						>
							{messages[language].quickLinks.faqs}
						</a>
						<a
							className={linkStyle}
							href={links.quickLinks.careers}
							target={"_blank"}
							rel="noreferrer"
						>
							{messages[language].quickLinks.careers}
						</a>
					</div>
					<div className={stackStyle}>
						<div className={headingStyle}>
							{messages[language].legal.heading}
						</div>
						<div className={underlineStyle}></div>
						<a
							className={linkStyle}
							href={links.legal.termsAndConditions}
							target={"_blank"}
							rel="noreferrer"
						>
							{messages[language].legal.termsAndConditions}
						</a>
						<a
							className={linkStyle}
							href={links.legal.termsOfUse}
							target={"_blank"}
							rel="noreferrer"
						>
							{messages[language].legal.termsOfUse}
						</a>
						<a
							className={linkStyle}
							href={links.legal.privacyPolicy}
							target={"_blank"}
							rel="noreferrer"
						>
							{messages[language].legal.privacyPolicy}
						</a>
					</div>
					<div className={stackStyle}>
						<div className={headingStyle}>
							{messages[language].getInTouch.heading}
						</div>
						<div className={underlineStyle}></div>
						<a
							className={linkStyle}
							href={links.getInTouch.phoneNumber}
							target={"_blank"}
							rel="noreferrer"
						>
							{messages[language].getInTouch.phoneNumber}
						</a>
						<a
							className={linkStyle}
							href={links.getInTouch.emailAddress}
							target={"_blank"}
							rel="noreferrer"
						>
							{messages[language].getInTouch.emailAddress}
						</a>
						<a
							className={addressStyle}
							href={links.getInTouch.physicalAddress}
							target={"_blank"}
							rel="noreferrer"
						>
							<span>{messages[language].getInTouch.addressLine1}</span>
							<span>{messages[language].getInTouch.addressLine2}</span>
						</a>
					</div>
				</div>
			</div>
			<hr className="mb-6 mt-9 opacity-30 footer-separator" />
			<div className={paragraphStyle}>
				<span>{messages[language].disclaimer1}</span>
				<span>{messages[language].disclaimer2}</span>
			</div>
		</div>
	);
});

export { FooterView };
